import { Typography } from '@material-ui/core';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConversationDropZoneProps {
    children: React.ReactNode;
    enabled?: boolean;
    onDrop: (files: File[]) => void;
}

export const ConversationDropZone = ({
    children,
    enabled = true,
    onDrop,
}: ConversationDropZoneProps) => {
    const [dragging, setDragging] = useState(false);
    const dragCounter = useRef(0);
    const { t } = useTranslation();

    const containsFiles = (event: React.DragEvent) =>
        Array.from(event.dataTransfer.types).includes('Files');

    const handleDragEnter = (event: React.DragEvent) => {
        if (!enabled || !containsFiles(event)) return;

        event.preventDefault();
        event.stopPropagation();

        dragCounter.current++;

        setDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        if (!enabled || !containsFiles(event)) return;

        event.preventDefault();
        event.stopPropagation();

        dragCounter.current--;

        if (dragCounter.current <= 0) {
            dragCounter.current = 0;
            setDragging(false);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        if (!enabled || !containsFiles(event)) return;

        event.preventDefault();
        event.stopPropagation();

        event.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (event: React.DragEvent) => {
        if (!enabled) return;

        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setDragging(false);

        const files = Array.from(event.dataTransfer.files);

        if (!files.length) return;

        onDrop(files);
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                flex: 1,
                overflow: 'hidden',
            }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {children}

            {dragging && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(0,0,0,.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        pointerEvents: 'none',
                        border: '2px dashed #ffffff66',
                    }}
                >
                    <Typography variant="h2" color="textPrimary">
                        {t('Chat.DropzonePlaceholder')}
                    </Typography>
                </div>
            )}
        </div>
    );
};
